import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, switchMap, catchError } from 'rxjs/operators';
import { Globals } from '../../global/globals';
import { ProjectService } from '../../services/project.service';
import { Project } from '../../dtos/project';
import { PaginatedResponse } from '../../dtos/paginated';
import { BaseModalComponent } from '../modals/base-modal/base-modal.component';

interface GroupPaper {
  id: number;
  title: string;
  authors: string;
  year: number | null;
  doi: string | null;
  alreadyInProject: boolean;
  venue: string | null;
  source: string;
  hasPdf: boolean;
  pdfId: number | null;
  pdfUrl: string | null;
}

interface PaperDetail {
  id: number;
  title: string;
  doi: string | null;
  abstractText: string | null;
  authors: string;
  venue: string | null;
  publicationDate: string | null;
  source: string;
  hasPdf: boolean;
  pdfId: number | null;
  pdfUrl: string | null;
}

const PAGE_SIZE = 25;
const DEBOUNCE_MS = 300;

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'OPENALEX', label: 'OpenAlex' },
  { value: 'BIBTEX', label: 'BibTeX' },
  { value: 'PDF', label: 'PDF Upload' },
] as const;

@Component({
  selector: 'app-group-papers',
  standalone: true,
  imports: [CommonModule, BaseModalComponent],
  templateUrl: './group-papers.component.html',
})
export class GroupPapersComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);
  private globals = inject(Globals);
  private projectService = inject(ProjectService);

  readonly query = signal('');
  readonly results = signal<PaginatedResponse<GroupPaper> | null>(null);
  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly currentPage = signal(0);
  readonly totalPages = signal(0);

  readonly projects = signal<Project[]>([]);
  readonly selectedProjectIds = signal<string[]>([]);
  readonly selectedSource = signal('');

  readonly showDetailModal = signal(false);
  readonly selectedPaper = signal<PaperDetail | null>(null);
  readonly detailLoading = signal(false);
  readonly detailError = signal<string | null>(null);

  readonly showPdfModal = signal(false);
  readonly selectedPdfTitle = signal('');
  readonly pdfViewerUrl = signal<SafeResourceUrl | null>(null);
  readonly pdfLoading = signal(false);
  readonly pdfError = signal('');
  private paperBlobUrl: string | null = null;

  protected readonly SOURCE_OPTIONS = SOURCE_OPTIONS;

  private groupId = '';
  private readonly search$ = new Subject<{
    q: string;
    page: number;
    projectIds: string[];
    source: string;
  }>();
  private sub?: Subscription;

  ngOnInit(): void {
    this.groupId = this.route.parent?.snapshot.params['id'];

    this.projectService.getProjectsByGroup(this.groupId).subscribe({
      next: (projects) => {
        this.projects.set(projects);
      },
      error: () => {},
    });

    this.sub = this.search$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        switchMap(({ q, page, projectIds, source }) => {
          this.loading.set(true);
          this.errorMessage.set(null);
          this.currentPage.set(page);
          return this.doSearch(q, page, projectIds, source);
        }),
      )
      .subscribe({
        next: (result) => {
          this.results.set(result);
          this.totalPages.set(result.totalPages);
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set('Failed to search papers. Please try again.');
          this.loading.set(false);
        },
      });

    this.triggerSearch();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.revokePdfBlobUrl();
  }

  private doSearch(q: string, page: number, projectIds: string[], source: string) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (projectIds.length > 0) params.set('projectIds', projectIds.join(','));
    if (source) params.set('source', source);
    params.set('page', String(page));
    params.set('size', String(PAGE_SIZE));
    const url = `${this.globals.backendUri}/groups/${this.groupId}/papers/search?${params}`;
    return this.http.get<PaginatedResponse<GroupPaper>>(url).pipe(
      catchError(() => {
        this.errorMessage.set('Failed to search papers. Please try again.');
        this.loading.set(false);
        return of({
          items: [],
          totalItems: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: PAGE_SIZE,
        });
      }),
    );
  }

  openDetail(paperId: number): void {
    this.detailLoading.set(true);
    this.detailError.set(null);
    this.selectedPaper.set(null);
    this.showDetailModal.set(true);

    const url = `${this.globals.backendUri}/groups/${this.groupId}/papers/${paperId}`;
    this.http.get<PaperDetail>(url).subscribe({
      next: (paper) => {
        this.selectedPaper.set(paper);
        this.detailLoading.set(false);
      },
      error: () => {
        this.detailError.set('Failed to load paper details.');
        this.detailLoading.set(false);
      },
    });
  }

  openPdf(paper: GroupPaper): void {
    this.selectedPdfTitle.set(paper.title || 'Untitled');
    if (paper.pdfId) {
      this.pdfError.set('');
      this.pdfViewerUrl.set(null);
      this.pdfLoading.set(true);
      this.showPdfModal.set(true);
      this.revokePdfBlobUrl();

      this.http
        .get(`${this.globals.backendUri}/groups/${this.groupId}/pdfs/${paper.pdfId}/content`, {
          responseType: 'blob',
        })
        .subscribe({
          next: (blob) => {
            this.paperBlobUrl = URL.createObjectURL(blob);
            this.pdfViewerUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.paperBlobUrl));
            this.pdfLoading.set(false);
          },
          error: () => {
            this.pdfError.set('Failed to load the PDF. Please try again.');
            this.pdfLoading.set(false);
          },
        });
      return;
    }

    if (paper.pdfUrl) {
      window.open(paper.pdfUrl, '_blank');
    }
  }

  closePdf(): void {
    this.showPdfModal.set(false);
    this.pdfViewerUrl.set(null);
    this.pdfError.set('');
    this.pdfLoading.set(false);
    this.revokePdfBlobUrl();
  }

  onQueryChange(value: string): void {
    this.query.set(value);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  onSourceChange(value: string): void {
    this.selectedSource.set(value);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  toggleProject(projectId: string): void {
    const ids = this.selectedProjectIds();
    if (ids.includes(projectId)) {
      this.selectedProjectIds.set(ids.filter((id) => id !== projectId));
    } else {
      this.selectedProjectIds.set([...ids, projectId]);
    }
    this.currentPage.set(0);
    this.triggerSearch();
  }

  clearProjects(): void {
    this.selectedProjectIds.set([]);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  goToPage(page: number): void {
    if (page < 0) return;
    this.search$.next({
      q: this.query().trim(),
      page,
      projectIds: this.selectedProjectIds(),
      source: this.selectedSource(),
    });
  }

  canPrev(): boolean {
    return this.currentPage() > 0;
  }

  canNext(): boolean {
    return this.currentPage() < this.totalPages() - 1;
  }

  private revokePdfBlobUrl(): void {
    if (this.paperBlobUrl) {
      URL.revokeObjectURL(this.paperBlobUrl);
      this.paperBlobUrl = null;
    }
  }

  private triggerSearch(): void {
    this.search$.next({
      q: this.query().trim(),
      page: 0,
      projectIds: this.selectedProjectIds(),
      source: this.selectedSource(),
    });
  }
}
